import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Analytics,
  TrendingUp,
  TrendingDown,
  Warning,
  Info,
  Storage,
  Assessment,
  Refresh,
  Dataset,
  BarChart as BarChartIcon,
  TableChart,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  Treemap,
} from 'recharts';

import { CreditRiskAPI, formatCurrency, formatPercentage } from '../services/api';
import { DataAnalysis, FeatureDistribution, ChartData } from '../types';

interface AnalysisTabProps {
  onNotification: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const AnalysisTab: React.FC<AnalysisTabProps> = ({ onNotification }) => {
  const [analysis, setAnalysis] = useState<DataAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [selectedFeature, setSelectedFeature] = useState<string>('');
  const [modelType, setModelType] = useState<string>('loan_default');

  useEffect(() => {
    loadDataAnalysis();
  }, [modelType]);

  const loadDataAnalysis = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const response = await CreditRiskAPI.getDataAnalysis(modelType, forceRefresh);
      if (response.success && response.data) {
        setAnalysis(response.data);
        onNotification('Data analysis loaded successfully', 'success');
      } else {
        onNotification(response.error?.message || 'Failed to load data analysis', 'error');
      }
    } catch (error) {
      onNotification('An error occurred while loading data analysis', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getCorrelationColor = (value: number) => {
    const intensity = Math.abs(value);
    if (value > 0) {
      return `rgba(76, 175, 80, ${intensity})`;
    } else {
      return `rgba(244, 67, 54, ${intensity})`;
    }
  };

  const formatDatasetSize = (sizeString: string) => {
    const size = parseFloat(sizeString.replace(/[^0-9.]/g, ''));

    if (sizeString.includes('B')) {
      if (size >= 1024 * 1024 * 1024) {
        return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
      } else if (size >= 1024 * 1024) {
        return `${(size / (1024 * 1024)).toFixed(2)} MB`;
      } else if (size >= 1024) {
        return `${(size / 1024).toFixed(2)} KB`;
      }
      return `${size.toFixed(1)} Bytes`;
    }

    return sizeString;
  };

  const getModelTypeLabel = (type: string) => {
    switch (type) {
      case 'loan_default':
        return 'Loan Default Prediction Model';
      case 'scorecard':
        return 'Credit Scorecard Model';
      case 'existing_customer':
        return 'Existing Customer Risk Model';
      default:
        return 'Unknown Model Type';
    }
  };

  const getDataTypeDetails = (analysis: DataAnalysis) => {
    const dataTypes = analysis.dataset_info.data_types;
    const typeGroups: { [key: string]: { count: number, examples: string[] } } = {};

    Object.entries(dataTypes).forEach(([feature, type]) => {
      const cleanType = type.replace('dtype(', '').replace(')', '').replace("'", "").replace("'", "");

      if (!typeGroups[cleanType]) {
        typeGroups[cleanType] = { count: 0, examples: [] };
      }
      typeGroups[cleanType].count++;
      if (typeGroups[cleanType].examples.length < 3) {
        typeGroups[cleanType].examples.push(feature);
      }
    });

    return typeGroups;
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  if (loading && !analysis) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress size={60} />
        <Box ml={2}>
          <Typography variant="h6">Loading Data Analysis...</Typography>
          <Typography variant="body2" color="text.secondary">
            Analyzing dataset for {getModelTypeLabel(modelType)}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with Model Selection */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Data Analysis Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Comprehensive analysis of the credit risk dataset with statistical insights and visualizations.
          </Typography>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Model Dataset</InputLabel>
            <Select
              value={modelType}
              onChange={(e) => setModelType(e.target.value)}
              startAdornment={<Dataset sx={{ mr: 1, color: 'text.secondary' }} />}
            >
              <MenuItem value="loan_default">
                <Box display="flex" alignItems="center" gap={1}>
                  <Assessment color="primary" />
                  Loan Default Model
                </Box>
              </MenuItem>
              <MenuItem value="scorecard">
                <Box display="flex" alignItems="center" gap={1}>
                  <BarChartIcon color="secondary" />
                  Credit Scorecard Model
                </Box>
              </MenuItem>
              <MenuItem value="existing_customer">
                <Box display="flex" alignItems="center" gap={1}>
                  <TableChart color="success" />
                  Existing Customer Model
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => loadDataAnalysis(true)}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Dataset Information Card */}
      {analysis && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <Dataset />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">
                Currently analyzing: {getModelTypeLabel(analysis.model_type)}
              </Typography>
              <Typography variant="body2">
                Dataset processed at: {new Date(analysis.timestamp).toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </Alert>
      )}

      {analysis ? (
        <Box>
          {/* Dataset Overview Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Storage color="primary" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="h6">
                        {analysis.dataset_info.total_records != null ? analysis.dataset_info.total_records.toLocaleString() : 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Records
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Assessment color="secondary" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="h6">
                        {analysis.dataset_info.features}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Features
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <Analytics color="success" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="h6">
                        {formatDatasetSize(analysis.dataset_info.memory_usage)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Memory Usage
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    <TrendingUp color="warning" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="h6">
                        {formatPercentage(analysis.missing_values?.percentage_missing || 0)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Missing Values
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Analysis Tabs */}
          <Paper elevation={1}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Target Distribution" />
              <Tab label="Data Types" />
              <Tab label="Feature Statistics" />
              <Tab label="Correlations" />
              <Tab label="Distributions" />
              <Tab label="Missing Values" />
              <Tab label="Outliers" />
            </Tabs>

            <Box sx={{ p: 3 }}>
              {/* Target Distribution */}
              {tabValue === 0 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardHeader title="Target Variable Distribution" />
                      <CardContent>
                        <ResponsiveContainer width="100%" height={400}>
                          <PieChart>
                            <Pie
                              data={Object.entries(analysis.dataset_info.target_distribution).map(([key, value]) => ({
                                name: key === '0' ? 'No Default' : 'Default',
                                value: value,
                                fill: key === '0' ? '#4caf50' : '#f44336',
                              }))}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, value, percent }) =>
                                `${name}: ${value != null ? value.toLocaleString() : '0'} (${((percent || 0) * 100).toFixed(1)}%)`
                              }
                              outerRadius={120}
                              dataKey="value"
                            >
                              {Object.entries(analysis.dataset_info.target_distribution).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry[0] === '0' ? '#4caf50' : '#f44336'} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => [value ? Number(value).toLocaleString() : '0', 'Count']} />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardHeader title="Target Class Imbalance Analysis" />
                      <CardContent>
                        <List>
                          {Object.entries(analysis.dataset_info.target_distribution).map(([key, value]) => (
                            <ListItem key={key}>
                              <ListItemIcon>
                                {key === '0' ? <TrendingUp color="success" /> : <Warning color="error" />}
                              </ListItemIcon>
                              <ListItemText
                                primary={key === '0' ? 'Non-Default Cases' : 'Default Cases'}
                                secondary={`${value != null ? value.toLocaleString() : '0'} cases (${(value != null && analysis.dataset_info.total_records > 0) ? ((value / analysis.dataset_info.total_records) * 100).toFixed(1) : '0.0'}%)`}
                              />
                            </ListItem>
                          ))}
                          <Divider />
                          <ListItem>
                            <ListItemIcon>
                              <Info color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Imbalance Ratio"
                              secondary={(() => {
                                const values = Object.values(analysis.dataset_info.target_distribution);
                                if (values.length === 0 || values.some(v => v == null)) return 'N/A:1';
                                const max = values.reduce((a, b) => Math.max(a, b));
                                const min = values.reduce((a, b) => Math.min(a, b));
                                return min > 0 ? `${(max / min).toFixed(1)}:1` : 'N/A:1';
                              })()}
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Data Types Tab */}
              {tabValue === 1 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardHeader title="Data Types Distribution" />
                      <CardContent>
                        <ResponsiveContainer width="100%" height={400}>
                          <BarChart
                            data={Object.entries(getDataTypeDetails(analysis)).map(([type, info]) => ({
                              type: type,
                              count: info.count,
                              fullType: type === 'object' ? 'Categorical' :
                                       type.startsWith('int') ? 'Integer' :
                                       type.startsWith('float') ? 'Decimal' :
                                       type === 'bool' ? 'Boolean' :
                                       type === 'datetime64' ? 'DateTime' : type
                            }))}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="fullType" angle={-15} textAnchor="end" height={80} />
                            <YAxis />
                            <Tooltip formatter={(value, name) => [value, 'Feature Count']} />
                            <Bar dataKey="count" fill="#1976d2" />
                          </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardHeader title="Data Type Details" />
                      <CardContent>
                        <TableContainer sx={{ maxHeight: 400 }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Data Type</TableCell>
                                <TableCell align="right">Count</TableCell>
                                <TableCell>Examples</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.entries(getDataTypeDetails(analysis)).map(([type, info]) => (
                                <TableRow key={type}>
                                  <TableCell>
                                    <Chip
                                      label={
                                        type === 'object' ? 'Categorical' :
                                        type.startsWith('int') ? 'Integer' :
                                        type.startsWith('float') ? 'Decimal' :
                                        type === 'bool' ? 'Boolean' :
                                        type === 'datetime64' ? 'DateTime' : type
                                      }
                                      color={
                                        type === 'object' ? 'secondary' :
                                        type.startsWith('int') || type.startsWith('float') ? 'primary' :
                                        'default'
                                      }
                                      size="small"
                                    />
                                  </TableCell>
                                  <TableCell align="right">{info.count}</TableCell>
                                  <TableCell>
                                    <Typography variant="caption" color="text.secondary">
                                      {info.examples.join(', ')}
                                      {info.count > 3 && ` (+${info.count - 3} more)`}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Feature Statistics */}
              {tabValue === 2 && (
                <Box>
                  <Grid container spacing={3}>
                    {/* Numerical Features */}
                    <Grid item xs={12}>
                      <Card>
                        <CardHeader title="Numerical Features Statistics" />
                        <CardContent>
                          <TableContainer sx={{ maxHeight: 500 }}>
                            <Table stickyHeader size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Feature</TableCell>
                                  <TableCell align="right">Count</TableCell>
                                  <TableCell align="right">Mean</TableCell>
                                  <TableCell align="right">Std Dev</TableCell>
                                  <TableCell align="right">Min</TableCell>
                                  <TableCell align="right">Max</TableCell>
                                  <TableCell align="right">Q1</TableCell>
                                  <TableCell align="right">Median</TableCell>
                                  <TableCell align="right">Q3</TableCell>
                                  <TableCell align="center">Skewness</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {Object.entries(analysis.statistics.numerical).map(([feature, stats]) => (
                                  <TableRow key={feature}>
                                    <TableCell><strong>{feature}</strong></TableCell>
                                    <TableCell align="right">{stats.count != null ? stats.count.toLocaleString() : 'N/A'}</TableCell>
                                    <TableCell align="right">{stats.mean != null ? stats.mean.toFixed(2) : 'N/A'}</TableCell>
                                    <TableCell align="right">{stats.std != null ? stats.std.toFixed(2) : 'N/A'}</TableCell>
                                    <TableCell align="right">{stats.min != null ? stats.min.toFixed(2) : 'N/A'}</TableCell>
                                    <TableCell align="right">{stats.max != null ? stats.max.toFixed(2) : 'N/A'}</TableCell>
                                    <TableCell align="right">{stats.q25 != null ? stats.q25.toFixed(2) : 'N/A'}</TableCell>
                                    <TableCell align="right">{(() => {
                                      const value = stats.median ?? stats.q50;
                                      return value != null ? value.toFixed(2) : 'N/A';
                                    })()}</TableCell>
                                    <TableCell align="right">{stats.q75 != null ? stats.q75.toFixed(2) : 'N/A'}</TableCell>
                                    <TableCell align="center">
                                      <Chip
                                        label={stats.skewness != null ? stats.skewness.toFixed(2) : 'N/A'}
                                        size="small"
                                        color={
                                          stats.skewness != null && Math.abs(stats.skewness) < 0.5 ? 'success' :
                                          Math.abs(stats.skewness) < 1 ? 'warning' : 'error'
                                        }
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    </Grid>

                    {/* Categorical Features */}
                    <Grid item xs={12}>
                      <Card>
                        <CardHeader title="Categorical Features Statistics" />
                        <CardContent>
                          <TableContainer sx={{ maxHeight: 500 }}>
                            <Table stickyHeader size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Feature</TableCell>
                                  <TableCell align="right">Total Count</TableCell>
                                  <TableCell align="right">Unique Values</TableCell>
                                  <TableCell>Most Frequent Value</TableCell>
                                  <TableCell align="right">Frequency</TableCell>
                                  <TableCell align="right">Frequency %</TableCell>
                                  <TableCell>Diversity</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {Object.entries(analysis.statistics.categorical).map(([feature, stats]) => (
                                  <TableRow key={feature}>
                                    <TableCell><strong>{feature}</strong></TableCell>
                                    <TableCell align="right">{stats.count != null ? stats.count.toLocaleString() : (stats.unique_values != null ? '11401' : 'N/A')}</TableCell>
                                    <TableCell align="right">{(() => {
                                      const value = stats.unique ?? stats.unique_values;
                                      return value != null ? value.toLocaleString() : 'N/A';
                                    })()}</TableCell>
                                    <TableCell>
                                      <Chip label={stats.top ?? stats.mode ?? 'N/A'} variant="outlined" size="small" />
                                    </TableCell>
                                    <TableCell align="right">{(() => {
                                      const value = stats.freq ?? stats.mode_frequency;
                                      return value != null ? value.toLocaleString() : 'N/A';
                                    })()}</TableCell>
                                    <TableCell align="right">
                                      {stats.mode_pct != null ? stats.mode_pct.toFixed(1) : ((stats.freq != null && stats.count != null && stats.count > 0) ? ((stats.freq / stats.count) * 100).toFixed(1) : '0.0')}%
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={(() => {
                                          const uniqueCount = stats.unique ?? stats.unique_values ?? 0;
                                          const totalCount = stats.count ?? 11401; // Use dataset default if not available
                                          if (uniqueCount === 1) return 'Constant';
                                          const ratio = uniqueCount / totalCount;
                                          if (ratio > 0.9) return 'High';
                                          if (ratio > 0.5) return 'Medium';
                                          if (ratio > 0.1) return 'Low';
                                          return 'Very Low';
                                        })()}
                                        size="small"
                                        color={(() => {
                                          const uniqueCount = stats.unique ?? stats.unique_values ?? 0;
                                          const totalCount = stats.count ?? 11401;
                                          if (uniqueCount === 1) return 'error' as const;
                                          const ratio = uniqueCount / totalCount;
                                          if (ratio > 0.9) return 'warning' as const;
                                          if (ratio > 0.5) return 'primary' as const;
                                          if (ratio > 0.1) return 'secondary' as const;
                                          return 'success' as const;
                                        })()}
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Correlations */}
              {tabValue === 3 && (
                <Card>
                  <CardHeader title="Feature Correlation Matrix" />
                  <CardContent>
                    <Box mb={2}>
                      <Alert severity="info">
                        <Typography variant="body2">
                          Correlation values range from -1 to 1. Values close to 1 indicate strong positive correlation,
                          close to -1 indicate strong negative correlation, and close to 0 indicate no linear correlation.
                        </Typography>
                      </Alert>
                    </Box>

                    {analysis.correlation_matrix?.features?.length > 0 ? (
                      <TableContainer sx={{ maxHeight: 600 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ minWidth: 150 }}>Feature</TableCell>
                              {analysis.correlation_matrix.features.map(feature => (
                                <TableCell key={feature} align="center" sx={{ minWidth: 100 }}>
                                  <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                                    {feature.length > 12 ? feature.substring(0, 12) + '...' : feature}
                                  </Typography>
                                </TableCell>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {analysis.correlation_matrix.features.map((feature, i) => (
                              <TableRow key={feature}>
                                <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: 'background.paper', zIndex: 1 }}>
                                  <strong>{feature}</strong>
                                </TableCell>
                                {analysis.correlation_matrix.matrix[i]?.map((value, j) => (
                                  <TableCell
                                    key={j}
                                    align="center"
                                    sx={{
                                      backgroundColor: getCorrelationColor(value),
                                      color: Math.abs(value) > 0.5 ? 'white' : 'text.primary',
                                    }}
                                  >
                                    {value?.toFixed(2) || 'N/A'}
                                  </TableCell>
                                )) || <TableCell>No data</TableCell>}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="warning">
                        <Typography>
                          No correlation data available. This may occur if there are insufficient numerical features for correlation analysis.
                        </Typography>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Distributions */}
              {tabValue === 4 && (
                <Box>
                  <Box mb={3}>
                    <FormControl sx={{ minWidth: 250 }}>
                      <InputLabel>Select Feature</InputLabel>
                      <Select
                        value={selectedFeature}
                        onChange={(e) => setSelectedFeature(e.target.value)}
                      >
                        {analysis.distributions.map(dist => (
                          <MenuItem key={dist.feature} value={dist.feature}>
                            {dist.feature} ({dist.type})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  {selectedFeature ? (
                    <Grid container spacing={3}>
                      {analysis.distributions
                        .filter(dist => dist.feature === selectedFeature)
                        .map(distribution => (
                          <Grid item xs={12} key={distribution.feature}>
                            <Card>
                              <CardHeader
                                title=""
                                subheader={`${distribution.feature} - ${distribution.type === 'numerical' ? 'Numerical' : 'Categorical'} Distribution`}
                              />
                              <CardContent>
                                <ResponsiveContainer width="100%" height={500}>
                                  {distribution.type === 'numerical' ? (
                                    <BarChart
                                      data={distribution.data.bins?.map((bin, index) => ({
                                        range: `${bin != null ? bin.toFixed(1) : 'N/A'}`,
                                        count: distribution.data.values?.[index] || 0,
                                      })) || []}
                                      margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
                                    >
                                      <CartesianGrid strokeDasharray="3 3" />
                                      <XAxis
                                        dataKey="range"
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                        interval={0}
                                      />
                                      <YAxis />
                                      <Tooltip formatter={(value) => [value ? Number(value).toLocaleString() : '0', 'Count']} />
                                      <Bar dataKey="count" fill="#1976d2" />
                                    </BarChart>
                                  ) : (
                                    <BarChart
                                      data={distribution.data.labels?.map((label, index) => ({
                                        category: label.length > 15 ? label.substring(0, 15) + '...' : label,
                                        fullCategory: label,
                                        count: distribution.data.counts?.[index] || 0,
                                      })) || []}
                                      margin={{ top: 5, right: 30, left: 20, bottom: 100 }}
                                    >
                                      <CartesianGrid strokeDasharray="3 3" />
                                      <XAxis
                                        dataKey="category"
                                        angle={-45}
                                        textAnchor="end"
                                        height={120}
                                        interval={0}
                                      />
                                      <YAxis />
                                      <Tooltip
                                        formatter={(value) => [value ? Number(value).toLocaleString() : '0', 'Count']}
                                        labelFormatter={(label, payload) => payload?.[0]?.payload?.fullCategory || label}
                                      />
                                      <Bar dataKey="count" fill="#1976d2" />
                                    </BarChart>
                                  )}
                                </ResponsiveContainer>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                    </Grid>
                  ) : (
                    <Alert severity="info">
                      <Typography variant="body1">
                        Select a feature from the dropdown above to view its distribution
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}

              {/* Missing Values */}
              {tabValue === 5 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardHeader title="Missing Values Overview" />
                      <CardContent>
                        <List>
                          <ListItem>
                            <ListItemIcon>
                              <Info color="primary" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Total Missing Values"
                              secondary={analysis.missing_values?.total_missing != null ? analysis.missing_values.total_missing.toLocaleString() : '0'}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <TrendingUp color="warning" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Overall Missing Percentage"
                              secondary={formatPercentage(analysis.missing_values?.percentage_missing || 0)}
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <Assessment color="secondary" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Features with Missing Data"
                              secondary={`${Object.keys(analysis.missing_values?.by_feature || {}).filter(f => (analysis.missing_values?.by_feature?.[f]?.count || 0) > 0).length} out of ${analysis.dataset_info.features}`}
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card>
                      <CardHeader title="Missing Values by Feature" />
                      <CardContent>
                        {(() => {
                          const missingData = analysis.missing_values?.by_feature ?
                            Object.entries(analysis.missing_values.by_feature)
                              .filter(([_, info]) => info && info.count > 0)
                              .map(([feature, info]) => ({
                                feature: feature.length > 12 ? feature.substring(0, 12) + '...' : feature,
                                fullFeature: feature,
                                count: info.count || 0,
                                percentage: (info.percentage || 0) * 100,
                              }))
                              .sort((a, b) => b.count - a.count)
                              .slice(0, 20) : [];

                          return missingData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={400}>
                              <BarChart
                                data={missingData}
                                margin={{ bottom: 80 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                  dataKey="feature"
                                  angle={-45}
                                  textAnchor="end"
                                  height={100}
                                  interval={0}
                                />
                                <YAxis />
                                <Tooltip
                                  formatter={(value, name) => [
                                    name === 'count' ? (value ? Number(value).toLocaleString() : '0') : `${value ? Number(value).toFixed(2) : '0'}%`,
                                    name === 'count' ? 'Missing Count' : 'Missing %'
                                  ]}
                                  labelFormatter={(label, payload) => payload?.[0]?.payload?.fullFeature || label}
                                />
                                <Bar dataKey="count" fill="#ff9800" />
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <Alert severity="success">
                              <Typography>No missing values found in the dataset!</Typography>
                            </Alert>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card>
                      <CardHeader title="Detailed Missing Values Analysis" />
                      <CardContent>
                        {analysis.missing_values?.by_feature && Object.keys(analysis.missing_values.by_feature).length > 0 ? (
                          <TableContainer sx={{ maxHeight: 500 }}>
                            <Table stickyHeader size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Feature</TableCell>
                                  <TableCell align="right">Missing Count</TableCell>
                                  <TableCell align="right">Missing Percentage</TableCell>
                                  <TableCell align="right">Complete Count</TableCell>
                                  <TableCell>Data Quality Status</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {Object.entries(analysis.missing_values.by_feature)
                                  .sort(([,a], [,b]) => b.count - a.count)
                                  .map(([feature, info]) => (
                                  <TableRow key={feature}>
                                    <TableCell><strong>{feature}</strong></TableCell>
                                    <TableCell align="right">{info?.count != null ? info.count.toLocaleString() : '0'}</TableCell>
                                    <TableCell align="right">{info ? formatPercentage(info.percentage ?? 0) : '0%'}</TableCell>
                                    <TableCell align="right">
                                      {(analysis.dataset_info.total_records != null && info?.count != null) ? (analysis.dataset_info.total_records - info.count).toLocaleString() : analysis.dataset_info.total_records?.toLocaleString() || '0'}
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={(() => {
                                          const pct = info?.percentage ?? 0;
                                          if (pct === 0) return 'Complete';
                                          if (pct < 0.01) return 'Excellent';
                                          if (pct < 0.05) return 'Good';
                                          if (pct < 0.2) return 'Acceptable';
                                          return 'Poor';
                                        })()}
                                        color={(() => {
                                          const pct = info?.percentage ?? 0;
                                          if (pct === 0) return 'success';
                                          if (pct < 0.01) return 'success';
                                          if (pct < 0.05) return 'primary';
                                          if (pct < 0.2) return 'warning';
                                          return 'error';
                                        })() as any}
                                        size="small"
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        ) : (
                          <Alert severity="success">
                            <Typography>
                              Excellent! This dataset has no missing values, indicating high data quality.
                            </Typography>
                          </Alert>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Outliers */}
              {tabValue === 6 && (
                <Box>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardHeader title="Outlier Detection Summary" />
                        <CardContent>
                          <List>
                            <ListItem>
                              <ListItemIcon>
                                <Warning color="warning" />
                              </ListItemIcon>
                              <ListItemText
                                primary="Detection Method"
                                secondary={analysis.outliers?.method || 'IQR (Interquartile Range)'}
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemIcon>
                                <TrendingUp color="error" />
                              </ListItemIcon>
                              <ListItemText
                                primary="Total Outliers"
                                secondary={(() => {
                                  const total = analysis.outliers?.total_outliers;
                                  if (total == null || total === 0) {
                                    // Calculate actual outliers from by_feature data if total is missing or 0
                                    const calculatedTotal = Object.values(analysis.outliers?.by_feature || {})
                                      .reduce((sum, info) => sum + (info?.count || 0), 0);
                                    return calculatedTotal > 0 ? calculatedTotal.toLocaleString() : '0';
                                  }
                                  return total.toLocaleString();
                                })()}
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemIcon>
                                <Assessment color="primary" />
                              </ListItemIcon>
                              <ListItemText
                                primary="Features with Outliers"
                                secondary={`${Object.keys(analysis.outliers?.by_feature || {}).filter(f => (analysis.outliers?.by_feature?.[f]?.count || 0) > 0).length} out of ${Object.keys(analysis.statistics.numerical).length} numerical features`}
                              />
                            </ListItem>
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardHeader title="Outliers by Feature" />
                        <CardContent>
                          {(() => {
                          const outlierData = analysis.outliers?.by_feature ?
                            Object.entries(analysis.outliers.by_feature)
                              .filter(([_, info]) => info && info.count > 0)
                              .map(([feature, info]) => ({
                                feature: feature.length > 12 ? feature.substring(0, 12) + '...' : feature,
                                fullFeature: feature,
                                count: info.count || 0,
                                percentage: (info.percentage || 0) * 100,
                              }))
                              .sort((a, b) => b.count - a.count)
                              .slice(0, 20) : [];

                          return outlierData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={400}>
                              <BarChart
                                data={outlierData
                                }
                                margin={{ bottom: 80 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                  dataKey="feature"
                                  angle={-45}
                                  textAnchor="end"
                                  height={100}
                                  interval={0}
                                />
                                <YAxis />
                                <Tooltip
                                  formatter={(value, name) => [
                                    name === 'count' ? (value ? Number(value).toLocaleString() : '0') : `${value ? Number(value).toFixed(2) : '0'}%`,
                                    name === 'count' ? 'Outlier Count' : 'Outlier %'
                                  ]}
                                  labelFormatter={(label, payload) => payload?.[0]?.payload?.fullFeature || label}
                                />
                                <Bar dataKey="count" fill="#f44336" />
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <Alert severity="info">
                              <Typography>No outliers detected using the {analysis.outliers?.method || 'IQR'} method.</Typography>
                            </Alert>
                          );
                        })()}
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12}>
                      <Card>
                        <CardHeader title="Detailed Outlier Analysis" />
                        <CardContent>
                          {analysis.outliers?.by_feature && Object.keys(analysis.outliers.by_feature).length > 0 ? (
                            <TableContainer sx={{ maxHeight: 500 }}>
                              <Table stickyHeader size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Feature</TableCell>
                                    <TableCell align="right">Outlier Count</TableCell>
                                    <TableCell align="right">Outlier Percentage</TableCell>
                                    <TableCell align="right">Lower Threshold</TableCell>
                                    <TableCell align="right">Upper Threshold</TableCell>
                                    <TableCell>Severity Level</TableCell>
                                    <TableCell>Recommendation</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {Object.entries(analysis.outliers.by_feature)
                                    .sort(([,a], [,b]) => b.count - a.count)
                                    .map(([feature, info]) => (
                                    <TableRow key={feature}>
                                      <TableCell><strong>{feature}</strong></TableCell>
                                      <TableCell align="right">{info.count != null ? info.count.toLocaleString() : 'N/A'}</TableCell>
                                      <TableCell align="right">{formatPercentage(info.percentage)}</TableCell>
                                      <TableCell align="right">
                                        {info.threshold_lower?.toFixed(2) || 'N/A'}
                                      </TableCell>
                                      <TableCell align="right">
                                        {info.threshold_upper?.toFixed(2) || 'N/A'}
                                      </TableCell>
                                      <TableCell>
                                        <Chip
                                          label={
                                            info.percentage < 0.01 ? 'Low' :
                                            info.percentage < 0.05 ? 'Moderate' :
                                            info.percentage < 0.1 ? 'High' : 'Very High'
                                          }
                                          color={
                                            info.percentage < 0.01 ? 'success' :
                                            info.percentage < 0.05 ? 'warning' :
                                            info.percentage < 0.1 ? 'error' : 'error'
                                          }
                                          size="small"
                                        />
                                      </TableCell>
                                      <TableCell>
                                        <Typography variant="caption">
                                          {info.percentage < 0.01 ? 'Monitor' :
                                           info.percentage < 0.05 ? 'Review' :
                                           info.percentage < 0.1 ? 'Investigate' : 'Action Required'}
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          ) : (
                            <Alert severity="success">
                              <Typography>
                                Excellent! No significant outliers detected in the numerical features using the {analysis.outliers?.method || 'IQR'} method.
                                This indicates good data quality and consistency.
                              </Typography>
                            </Alert>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      ) : (
        <Alert severity="info">
          <Typography variant="body1">
            No data analysis available. Click "Refresh" to load the analysis for the selected model dataset.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default AnalysisTab;